import { MenuItem, TextField } from '@mui/material';
import {
  selectableReasons,
  type TradeTransferKind,
  type TradeTransferReason,
} from '../tradeReasons/tradeReasonsApi';

/**
 * Powód z listy i komentarz przy przekazaniu do Trade i zwrocie do FTL
 * (makieta back office v2, A6: BR-FTL-03, BR-TRD-11; przełącznik
 * `tradeTransferReasons`, 20.10.2026). Zastępuje pole „Powód” w wierszu
 * „Zmień stan”, gdy wybrany stan to przekazanie albo zwrot — inne zmiany
 * stanu mają powód tekstem, jak dotąd.
 *
 * Wymagany — tak jak dotąd mówi słownik przejść (`requiresReason` z API):
 * zwrot z Trade i przekazanie obsadzonego zlecenia. Wtedy sam komentarz nie
 * wystarcza — API odmówi (BR-TRD-11: „powód z listy”).
 */

/** Wartość „bez powodu z listy” w polu wyboru. */
export const NO_REASON = '';

const ROW_FIELD_SX = { flex: '1 1 160px', maxWidth: 480, minWidth: 0 } as const;

/** Czy można wysłać: wymagany kod jest, a powód „inny” ma komentarz. */
export function transferReasonReady(input: {
  required: boolean;
  code: string;
  comment: string;
  reasons: readonly TradeTransferReason[];
}): boolean {
  if (input.required && input.code === NO_REASON) return false;
  const chosen = input.reasons.find((r) => r.code === input.code);
  if (chosen?.requiresComment && input.comment.trim() === '') return false;
  return true;
}

export const TransferReasonFields = ({
  kind,
  required,
  reasons,
  code,
  comment,
  onCode,
  onComment,
  disabled,
}: {
  kind: TradeTransferKind;
  required: boolean;
  /** `null` — słownik się wczytuje. */
  reasons: readonly TradeTransferReason[] | null;
  code: string;
  comment: string;
  onCode: (code: string) => void;
  onComment: (comment: string) => void;
  disabled: boolean;
}) => {
  const options = selectableReasons(reasons ?? [], kind);
  const chosen = options.find((r) => r.code === code);
  const what = kind === 'handover' ? 'przekazania do Trade' : 'zwrotu do FTL';
  return (
    <>
      <TextField
        select
        size="small"
        label={required ? `Powód ${what} (wymagany)` : `Powód ${what}`}
        required={required}
        value={options.some((r) => r.code === code) ? code : NO_REASON}
        onChange={(e) => onCode(e.target.value)}
        disabled={disabled || reasons === null}
        sx={ROW_FIELD_SX}
        helperText={
          reasons === null
            ? 'Wczytuję listę powodów…'
            : options.length === 0
              ? 'Lista powodów jest pusta — poproś administratora o jej uzupełnienie'
              : 'Z listy; trafi do historii zmian'
        }
        slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
        data-testid="transfer-reason"
      >
        <MenuItem value={NO_REASON} disabled={required}>
          {required ? 'Wybierz powód' : '— bez powodu —'}
        </MenuItem>
        {options.map((r) => (
          <MenuItem key={r.code} value={r.code}>
            {r.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label={chosen?.requiresComment ? 'Komentarz (wymagany)' : 'Komentarz (opcjonalny)'}
        required={chosen?.requiresComment === true}
        value={comment}
        onChange={(e) => onComment(e.target.value)}
        disabled={disabled}
        sx={ROW_FIELD_SX}
        helperText={chosen?.requiresComment ? 'Opisz powód — bez tego nic nie mówi' : ' '}
        slotProps={{ htmlInput: { maxLength: 500 } }}
        data-testid="transfer-comment"
      />
    </>
  );
};
